import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useLogo } from '../contexts/LogoContext';
import { ArrowLeft, UserCheck, Sparkles, Phone, User, Mail } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  
  // Google sign-in profile completion modal state (phone collection)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [googleProfileData, setGoogleProfileData] = useState({
    uid: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'member'
  });

  const { signInWithGoogle, updateUserProfile } = useAuth();
  const { logoUrl } = useLogo();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';
  const search = location.state?.from?.search || '';
  const redirectPath = from + search;

  const handleGoogleProfileChange = (e) => {
    const { name, value } = e.target;
    setGoogleProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper to handle navigation after login/registration
  const handleRedirect = (userData) => {
    const isUserAdmin = userData?.role === 'admin' || userData?.isAdmin === true;

    // 1. If a normal user somehow tries to redirect to an admin page, force them to Home
    if (redirectPath?.startsWith('/admin') && !isUserAdmin) {
      navigate('/', { replace: true });
      return;
    }

    // 2. Safely redirect to their intended page (like Find My Photos or Member Portal)
    if (redirectPath && redirectPath !== '/') {
      navigate(redirectPath, { replace: true });
    } 
    // 3. Admin default goes to Admin Panel
    else if (isUserAdmin) {
      navigate('/admin', { replace: true });
    } 
    // 4. Normal user default goes to Home Page
    else {
      navigate('/', { replace: true });
    }
  };

  // Format Firebase error codes into friendly user messages
  const getFirebaseErrorMessage = (error) => {
    switch (error.code) {
      case 'auth/email-already-in-use':
        return 'An account with this email address already exists. Please sign in instead.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/weak-password':
        return 'Password must be at least 6 characters long.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Incorrect email or password. Please try again.';
      case 'auth/too-many-requests':
        return 'Too many unsuccessful attempts. Please try again later.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in window was closed. Please try again.';
      case 'auth/popup-blocked':
        return 'Pop-up was blocked by your browser. Please allow pop-ups for this site.';
      case 'auth/unauthorized-domain':
        return 'This domain is not authorized for sign-in.';
      case 'auth/network-request-failed':
        return 'Network connection error. Please verify your internet connection.';
      default:
        return error.message || 'Authentication failed. Please try again.';
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      const userData = await signInWithGoogle();

      // Check if phone number is present (at least 10 digits)
      const phoneDigits = (userData?.phone || '').replace(/\D/g, '');
      if (!phoneDigits || phoneDigits.length < 10) {
        // Prompt user to provide phone number & confirm first/last name
        setGoogleProfileData({
          uid: userData?.uid || userData?.id || '',
          email: userData?.email || '',
          firstName: userData?.firstName || userData?.displayName?.split(' ')[0] || '',
          lastName: userData?.lastName || userData?.displayName?.split(' ').slice(1).join(' ') || '',
          phone: userData?.phone || '',
          role: userData?.role || 'member'
        });
        setIsProfileModalOpen(true);
        return;
      }

      toast.success('Successfully signed in with Google!');
      handleRedirect(userData);
    } catch (error) {
      console.error('Google sign-in error:', error);
      toast.error(getFirebaseErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoogleProfile = async (e) => {
    e.preventDefault();

    if (!googleProfileData.firstName.trim() || !googleProfileData.lastName.trim()) {
      toast.error('First and last name are required.');
      return;
    }

    const phoneDigits = googleProfileData.phone.replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length < 10) {
      toast.error('Please enter a valid phone number (at least 10 digits).');
      return;
    }

    try {
      setProfileSaving(true);
      const updatedUser = await updateUserProfile(googleProfileData.uid || googleProfileData.email, {
        firstName: googleProfileData.firstName.trim(),
        lastName: googleProfileData.lastName.trim(),
        phone: googleProfileData.phone.trim(),
        email: googleProfileData.email
      });

      setIsProfileModalOpen(false);
      toast.success('Profile saved! Welcome to Beats of Washington.');
      handleRedirect(updatedUser || { ...googleProfileData, role: googleProfileData.role || 'member' });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign In - Beats of Washington</title>
        <meta 
          name="description" 
          content="Sign in with Google to access your Beats of Washington member portal and Find My Photos." 
        />
      </Helmet>

      <div className="min-h-screen flex flex-col justify-start sm:justify-center pt-8 pb-12 sm:py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Mountain Background Image */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url("https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")`
            }}
          ></div>
          <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[2px]"></div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-48 h-48 md:w-80 md:h-80 bg-gradient-to-br from-orange-400/20 to-amber-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 md:w-96 md:h-96 bg-gradient-to-br from-amber-400/20 to-yellow-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="mx-auto w-full max-w-md relative z-10 pt-4 sm:pt-0">
          {/* Logo */}
          <div className="flex justify-center">
            <Link to="/" className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-500 via-amber-600 to-yellow-600 rounded-full flex items-center justify-center overflow-hidden shadow-2xl hover:scale-105 transition-transform duration-300">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="BOW Logo" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-bold text-2xl">B</span>
              )}
            </Link>
          </div>

          <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-white tracking-tight drop-shadow-md">
            Welcome to Beats of Washington
          </h2>
          <p className="mt-2 text-center text-sm text-amber-100/90 drop-shadow">
            Sign in with your Google account to access member features
          </p>

          {redirectPath?.startsWith('/find-my-photos') && (
            <div className="mt-4 mx-2 p-3 rounded-xl bg-orange-500/20 border border-orange-400/40 text-orange-100 text-xs text-center backdrop-blur-sm">
              <span className="font-semibold">📸 Find My Photos:</span> Please sign in with Google to access the personal photo search experience.
            </div>
          )}

          {(redirectPath?.startsWith('/bid-for-divine-blessings') || redirectPath?.startsWith('/auction') || redirectPath?.startsWith('/bidding')) && (
            <div className="mt-4 mx-2 p-3 rounded-xl bg-orange-500/20 border border-orange-400/40 text-orange-100 text-xs text-center backdrop-blur-sm">
              <span className="font-semibold">🌺 Sacred Auction:</span> Please sign in with Google to place your devotional bid.
            </div>
          )}
        </div>

        <div className="mt-8 sm:mt-6 mx-auto w-full max-w-md relative z-10">
          <div className="bg-white/95 backdrop-blur-md py-7 px-5 sm:px-8 shadow-2xl rounded-2xl border border-white/40">
            {/* Google Sign-In Button */}
            <div>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full flex justify-center items-center px-4 py-3 border border-gray-200 rounded-xl shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:shadow transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3 flex-shrink-0" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span>Continue with Google</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center relative z-10 px-4">
          <Link
            to="/"
            className="inline-flex items-center text-xs sm:text-sm text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to home
          </Link>
        </div>

        {/* Google Sign-In: Complete Profile / Phone Number Modal */}
        {isProfileModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-950/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-gray-100 relative animate-fade-in">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-white border border-gray-100 flex items-center justify-center shadow-sm overflow-hidden p-1.5">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl text-white flex items-center justify-center">
                      <UserCheck className="w-5 h-5" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Complete Your Profile
                  </h3>
                  <p className="text-xs text-gray-500">
                    Required for Beats of Washington members
                  </p>
                </div>
              </div>

              <p className="text-xs sm:text-sm text-gray-600 mb-5 leading-relaxed">
                Welcome to Beats of Washington! To complete your registration and enable photo search access, please provide your contact phone number and confirm your name.
              </p>

              <form onSubmit={handleSaveGoogleProfile} className="space-y-4">
                {/* Google Email Display */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Google Account
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                      <Mail className="h-4 w-4" />
                    </div>
                    <input
                      type="email"
                      readOnly
                      disabled
                      value={googleProfileData.email}
                      className="w-full pl-9 pr-3 py-2 text-sm bg-gray-100 text-gray-600 border border-gray-200 rounded-lg cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="googleFirstName" className="block text-xs font-semibold text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <User className="h-4 w-4" />
                      </div>
                      <input
                        id="googleFirstName"
                        name="firstName"
                        type="text"
                        required
                        value={googleProfileData.firstName}
                        onChange={handleGoogleProfileChange}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="First name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="googleLastName" className="block text-xs font-semibold text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <User className="h-4 w-4" />
                      </div>
                      <input
                        id="googleLastName"
                        name="lastName"
                        type="text"
                        required
                        value={googleProfileData.lastName}
                        onChange={handleGoogleProfileChange}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                </div>

                {/* Phone Number Field */}
                <div>
                  <label htmlFor="googlePhone" className="block text-xs font-semibold text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                      <Phone className="h-4 w-4" />
                    </div>
                    <input
                      id="googlePhone"
                      name="phone"
                      type="tel"
                      required
                      value={googleProfileData.phone}
                      onChange={handleGoogleProfileChange}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="(425) 555-0199"
                      autoFocus
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-gray-500">
                    Enter at least 10 digits. Used for member verification & event notifications.
                  </p>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={profileSaving}
                    className="w-full bg-gradient-to-r from-orange-500 via-amber-600 to-yellow-600 hover:from-orange-600 hover:via-amber-700 hover:to-yellow-700 text-white py-3 px-4 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    {profileSaving ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        <span>Save & Complete Profile</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LoginPage;